import socketIo from 'socket.io-client';
import { head, pipe, pluck } from 'ramda';
import { addRelativeUri, parseMessage, prepareOptions, updateCorrelationId } from './helpers';
import { getBaseDomain } from 'js/utils/url.util';

export const defaultOptions = {
    requestTimeout: 30000,
    reconnectionDelay: 3000,
    maxReconnectionAttempts: Infinity,
    reconnectionLimit: 3000,
    connectTimeout: 15000,
    forceNewConnection: true,
    reconnect: true,
    transports: ['websocket', 'xhr-polling']
};

export const connect = (url, options = {}) => {
    const mergedOptions = { ...defaultOptions, ...options };
    const endpoint = url.replace('[superdomain]', getBaseDomain());
    const io = socketIo.connect(endpoint, prepareOptions(mergedOptions));

    return {
        io,
        url,
        options: mergedOptions,
        stack: []
    };
};

export const disconnect = (connection) => {
    connection.io.disconnect();
    return { ...connection, stack: [] };
};

const createRequestStackObject = ({ connection, request, resolve: outerResolve, reject: outerReject }) => {
    const timeout = connection.options.requestTimeout;
    let resolve = outerResolve;
    let reject = outerReject;
    let promise = null;

    if (!resolve) {
        promise = new Promise((innerResolve, innerReject) => {
            resolve = innerResolve;
            reject = innerReject;
        });
    }

    const stackRequest = {
        request,
        resolve,
        reject,
        promise,
        timestamp: new Date(),
        timer: setTimeout(() => {
            // request is removed from stack in server.send on promise rejection
            reject({
                reason: 'Request timed out',
                stackRequest
            });
        }, timeout)
    };

    return stackRequest;
};

const handleRequest = (params) => {
    const { nextStack, request, connection, resolve, reject } = params;

    // batch request
    if (request.ID === 35508 && request.requests && request.requests.length > 0) {
        const batchRequestStackObj = createRequestStackObject({ connection, request });
        const requestStackObjects = request.requests.map(req => createRequestStackObject({
            connection,
            request: req
        }));

        Promise.all([batchRequestStackObj.promise, ...pluck('promise', requestStackObjects)])
            .then(resolve)
            .catch(reject);

        return [...nextStack, batchRequestStackObj, ...requestStackObjects];
    }

    // single request
    const stackRequest = createRequestStackObject(params);

    return [...nextStack, stackRequest];
};

export const send = (connection, request) => {
    const { stack } = connection;
    let nextStack = stack;
    const updatedRequest = pipe(
        updateCorrelationId,
        addRelativeUri
    )(request);

    const promise = new Promise((resolve, reject) => {
        if (!connection || !connection.io.socket.connected) {
            return reject({
                reason: 'There is no connection',
                request
            });
        }

        // NB If we are to use correlationId instead of responses - this will break things
        if (updatedRequest.responses) {
            nextStack = handleRequest({
                connection,
                request: updatedRequest,
                nextStack,
                resolve,
                reject
            });
        }

        connection.io.send(JSON.stringify(updatedRequest));
    });

    return { nextConnection: { ...connection, stack: nextStack }, promise };
};

export const findRequestByMessageId = (stack, ID) => stack.find(r => r.request.responses.includes(ID));

const discardRequest = (stack, stackRequest) => {
    window.clearTimeout(stackRequest.timer);
    return stack.filter(stackReq => stackReq !== stackRequest);
};

export const handleMessage = (connection, rawMessage) => {
    const { stack } = connection;
    const message = parseMessage(rawMessage);
    const responseCorrelationId = message.correlationId || message.data && message.data.correlationId;
    let nextStack = stack;
    let filteredStackByCorrelationId;
    let stackRequest;
    let request;
    let response;
    let pushMessage;
    let error;
    let time;

    if (responseCorrelationId) {
        filteredStackByCorrelationId = stack.filter(
            stackReq => stackReq.request.correlationId === responseCorrelationId
        );
        stackRequest = findRequestByMessageId(filteredStackByCorrelationId, message.ID)
            || head(filteredStackByCorrelationId);
    } else {
        // Assuming we cannot have requests without correlationId, having a response without one means this
        // response doesn't support correlationId and we may safely take first match by response ID
        stackRequest = findRequestByMessageId(stack, message.ID);
    }

    if (stackRequest) {
        const { timestamp } = stackRequest;
        if (timestamp) {
            time = new Date() - timestamp;
        }

        ({ request } = stackRequest);
        response = message;
        nextStack = discardRequest(nextStack, stackRequest);

        // handle error response
        if (response.data && (response.data.error || response.data.errorCode)) {
            error = message;

            stackRequest.reject({
                reason: 'Request error', // todo extract error message from error response for logger
                request,
                response,
                time
            });

            if (stackRequest.request.ID === 35508) {
                // discard requests in batch
                filteredStackByCorrelationId.forEach((stackReq) => {
                    nextStack = discardRequest(nextStack, stackReq);
                });
            }
        } else {
            stackRequest.resolve(response);
        }
    } else {
        pushMessage = message;
    }

    const nextConnection = stack === nextStack
        ? connection
        : { ...connection, stack: nextStack };

    return {
        nextConnection,
        request,
        response,
        pushMessage,
        error,
        time
    };
};
